import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Container, Row, Col, Card, CardBody,
  Spinner, Button, Form, FormGroup, Label, Input,
} from "reactstrap";
import axios from "axios";
import ExpenseSummaryDashboard from "./ExpenseSummary";
import ExpenseHeader from "./ExpenseHeader";
import PageGrid from "../../components/GridLayout/PageGrid";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import FieldCustomizerPanel, { useFieldCustomizer, DataTable } from "../../components/FieldCustomizer/FieldCustomizer";
import "../../components/FieldCustomizer/FieldCustomizer.css";

const EXPENSE_FIELDS = [
  { value:"date",           label:"Date"           },
  { value:"person_name",    label:"Person Name"    },
  { value:"project_name",   label:"Project"        },
  { value:"category_name",  label:"Category"       },
  { value:"remarks",        label:"Remarks"        },
  { value:"supplier",       label:"Supplier"       },
  { value:"total_cost",     label:"Amount (₹)"     },
  { value:"payment_status", label:"Payment Status" },
];

const EXPENSE_DEFAULT_COLS = [
  { id:"date",           label:"Date",           field:"date",           visible:true },
  { id:"person_name",    label:"Name",           field:"person_name",    visible:true },
  { id:"project_name",   label:"Project",        field:"project_name",   visible:true },
  { id:"category_name",  label:"Category",       field:"category_name",  visible:true },
  { id:"remarks",        label:"Remarks",        field:"remarks",        visible:true },
  { id:"supplier",       label:"Supplier",       field:"supplier",       visible:false },
  { id:"total_cost",     label:"Amount (₹)",     field:"total_cost",     visible:true },
  { id:"payment_status", label:"Payment",        field:"payment_status", visible:true },
];

const ExpenseDashboard = () => {
  const navigate = useNavigate();
  const [users,            setUsers]           = useState([]);
  const [expenses,         setExpenses]        = useState([]);
  const [categories,       setCategories]      = useState([]);
  const [projects,         setProjects]        = useState([]);
  const [loading,          setLoading]         = useState(true);
  const [selectedExpenses, setSelectedExpenses]= useState([]);
  const [totalSelectedAmount,setTotalSelectedAmount]=useState(0);
  const [expenseDetail,    setExpenseDetail]   = useState(null);
  const [detailLoading,    setDetailLoading]   = useState(false);
  const [modalOpen,        setModalOpen]       = useState(false);
  const userName = localStorage.getItem("name");

  const tableFC = useFieldCustomizer("expense_table", [], EXPENSE_DEFAULT_COLS);

  const [filters, setFilters] = useState({ category:"", person:"", project:"", payment_status:"", start_date:"", end_date:"" });

  useEffect(() => {
    axios.get("https://api.hanoch.in/expense/project_categories/").then(r=>setProjects(Array.isArray(r.data)?r.data:[])).catch(console.error);
    axios.get("https://api.hanoch.in/users/clients/?is_active=true").then(r=>setUsers(r.data||[])).catch(console.error);
    axios.get("https://api.hanoch.in/expense/categories/").then(r=>setCategories(Array.isArray(r.data)?r.data:[])).catch(console.error);
    fetchExpenses();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      const active = Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
      fetchExpenses(active);
    }, 500);
    return () => clearTimeout(delay);
  }, [filters]);

  const fetchExpenses = async (filterParams={}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filterParams).toString();
      const res = await axios.get(`https://api.hanoch.in/expense/?${params}`);
      setExpenses(res?.results || res.data || []);
    } catch(e){ console.error(e); } finally { setLoading(false); }
  };

  const fetchExpenseDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`https://api.hanoch.in/expense/company-expense-detail-view/${id}/`);
      setExpenseDetail(res.data); setModalOpen(true);
    } catch(e){ console.error(e); } finally { setDetailLoading(false); }
  };

  const handleFilterChange = (e) => { const {name,value}=e.target; setFilters(p=>({...p,[name]:value})); };
  const resetFilters = () => { setFilters({category:"",person:"",project:"",payment_status:"",start_date:"",end_date:""}); fetchExpenses(); };
  const handleSelect = (id, amount) => setSelectedExpenses(prev=>prev.some(i=>i.id===id)?prev.filter(i=>i.id!==id):[...prev,{id,amount:parseFloat(amount)}]);

  useEffect(()=>{ setTotalSelectedAmount(selectedExpenses.reduce((s,e)=>s+e.amount,0)); }, [selectedExpenses]);

  /* ── Widget content ─────────────────────────────────────────────── */
  const SummaryWidget = (
    <Card className="h-100 mb-0"><CardBody><ExpenseHeader/><ExpenseSummaryDashboard/></CardBody></Card>
  );

  const FiltersWidget = (
    <Card className="h-100 mb-0">
      <CardBody>
        <Form>
          <Row className="align-items-end g-2">
            <Col md={2}><FormGroup><Label>Name</Label>
              <Input type="select" name="person" value={filters.person} onChange={handleFilterChange}>
                <option value="">All</option>{users.map(u=><option key={u.id} value={u.name}>{u.name}</option>)}
              </Input></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Category</Label>
              <Input type="select" name="category" value={filters.category} onChange={handleFilterChange}>
                <option value="">All</option>{categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
              </Input></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Project</Label>
              <Input type="select" name="project" value={filters.project} onChange={handleFilterChange}>
                <option value="">All</option>{projects.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
              </Input></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Payment</Label>
              <Input type="select" name="payment_status" value={filters.payment_status} onChange={handleFilterChange}>
                <option value="">All</option><option value="paid">Paid</option><option value="unpaid">Unpaid</option>
              </Input></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Start Date</Label><Input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange}/></FormGroup></Col>
            <Col md={2}><FormGroup><Label>End Date</Label><Input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange}/></FormGroup></Col>
          </Row>
          <Row className="mt-1"><Col className="d-flex justify-content-end">
            <Button color="secondary" size="sm" onClick={resetFilters}>Reset Filters</Button>
          </Col></Row>
        </Form>
      </CardBody>
    </Card>
  );

  const ActionsWidget = (
    <Card className="h-100 mb-0">
      <CardBody className="d-flex flex-wrap align-items-center gap-2 p-2">
        <Button color="primary" size="sm" onClick={()=>navigate("/addExpense")}>+ Add Expense</Button>
        <Button color="outline-primary" size="sm" onClick={()=>navigate("/addExpenseUser")}>User</Button>
        <Button color="outline-primary" size="sm" onClick={()=>navigate("/addExpenseCategory")}>Category</Button>
        <Button color="outline-primary" size="sm" onClick={()=>navigate("/addProject")}>Project</Button>
        {selectedExpenses.length>0&&(
          <div className="ms-auto d-flex align-items-center gap-3">
            <span className="fw-bold">Selected: ₹{totalSelectedAmount.toFixed(2)}</span>
            {expenses.some(item=>selectedExpenses.some(sel=>sel.id===item.id)&&(userName==="Hanoch Admin"||item.person_name===userName))&&(
              <Button color="success" size="sm" onClick={()=>navigate("/uploadPaymentProof",{state:{selectedExpenses}})}>
                Proceed to Payment
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );

  // Custom row renderer with checkbox + edit button
  const TableWidget = (
    <Card className="h-100 mb-0">
      <CardBody style={{padding:0, height:"100%", overflow:"auto"}}>
        {loading
          ? <div className="d-flex justify-content-center align-items-center" style={{height:200}}><Spinner color="primary"/></div>
          : (() => {
              const visibleCols = tableFC.columns.filter(c=>c.visible!==false);
              return (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered mb-0" style={{fontSize:13}}>
                    <thead className="table-light" style={{position:"sticky",top:0,zIndex:1}}>
                      <tr>
                        <th><input type="checkbox"
                          checked={selectedExpenses.length===expenses.length&&expenses.length>0}
                          onChange={e=>setSelectedExpenses(e.target.checked?expenses.map(i=>({id:i.id,amount:parseFloat(i.total_cost)})):[])}/></th>
                        {visibleCols.map(col=><th key={col.id}>{col.label}</th>)}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((item,i)=>(
                        <tr key={item.id} style={{cursor:"pointer"}} onClick={()=>fetchExpenseDetail(item.id)}>
                          <td onClick={e=>e.stopPropagation()}>
                            <input type="checkbox" checked={selectedExpenses.some(e=>e.id===item.id)} onChange={()=>handleSelect(item.id,item.total_cost)}/>
                          </td>
                          {visibleCols.map(col=>(
                            <td key={col.id}>
                              {col.field==="payment_status"
                                ? <span className={`px-2 py-1 rounded-pill text-white fw-semibold ${item.payment_status?.toLowerCase()==="paid"?"bg-success":"bg-danger"}`} style={{fontSize:"0.75rem"}}>{item[col.field]}</span>
                                : item[col.field]??'—'}
                            </td>
                          ))}
                          <td onClick={e=>e.stopPropagation()}>
                            <Button color="link" className="p-0 border-0 shadow-none" onClick={()=>fetchExpenseDetail(item.id)}>
                              <FiEdit2 size={15} color="#0d6efd"/>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()
        }
      </CardBody>
    </Card>
  );

  const widgets = [
    { id:"summary", label:"Summary",      icon:"mdi mdi-chart-box-outline", defaultW:12, defaultH:5,  minH:4, content:SummaryWidget },
    { id:"filters", label:"Filters",      icon:"mdi mdi-filter-outline",    defaultW:12, defaultH:5,  minH:4, content:FiltersWidget },
    { id:"actions", label:"Actions",      icon:"mdi mdi-plus-box-outline",  defaultW:12, defaultH:3,  minH:2, content:ActionsWidget },
    {
      id:"table", label:"Expense Table",  icon:"mdi mdi-table",             defaultW:12, defaultH:10, minH:6,
      customizer:(
        <FieldCustomizerPanel cards={[]} columns={tableFC.columns} availableFields={EXPENSE_FIELDS}
          onCardsChange={()=>{}} onColumnsChange={tableFC.updateCols} onReset={tableFC.resetFields}/>
      ),
      content: TableWidget,
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="OptiMize" breadcrumbItem="Expense Dashboard" />
          <PageGrid pageKey="expense_dashboard" widgets={widgets} />
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={()=>setModalOpen(!modalOpen)} size="lg" centered>
        <ModalHeader toggle={()=>setModalOpen(false)}><strong>Expense Details</strong></ModalHeader>
        <ModalBody>
          {detailLoading?<div className="text-center my-4"><Spinner color="primary"/></div>:expenseDetail?(
            <div className="px-2">
              <Row className="mb-3">
                <Col md="6"><strong>Date:</strong><p className="text-muted mb-0">{expenseDetail.date}</p></Col>
                <Col md="6"><strong>Person:</strong><p className="text-muted mb-0">{expenseDetail.person_name}</p></Col>
              </Row>
              <Row className="mb-3">
                <Col md="6"><strong>Category:</strong><p className="text-muted mb-0">{expenseDetail.category_name}</p></Col>
                <Col md="6"><strong>Supplier:</strong><p className="text-muted mb-0">{expenseDetail.supplier}</p></Col>
              </Row>
              <Row className="mb-3">
                <Col md="6"><strong>Total Cost:</strong><p className="text-success fw-bold mb-0">₹{expenseDetail.total_cost}</p></Col>
                <Col md="6"><strong>Payment:</strong><p className={`fw-semibold mb-0 ${expenseDetail.payment_status==="Paid"?"text-success":"text-danger"}`}>{expenseDetail.payment_status}</p></Col>
              </Row>
              <Row className="mb-3"><Col><strong>Remarks:</strong><p className="text-muted mb-0">{expenseDetail.remarks||"—"}</p></Col></Row>
              {(expenseDetail.bill_image||expenseDetail.payment_proof)&&(
                <Row className="mt-3">
                  {expenseDetail.bill_image&&<Col md="6" className="text-center mb-3"><strong>Bill:</strong><div className="mt-2">
                    {expenseDetail.bill_image.toLowerCase().endsWith(".pdf")?<Button color="primary" size="sm" onClick={()=>window.open(expenseDetail.bill_image,"_blank")}>📄 View PDF</Button>:<img src={expenseDetail.bill_image} alt="Bill" className="img-fluid rounded" style={{maxHeight:200}}/>}
                  </div></Col>}
                  {expenseDetail.payment_proof&&<Col md="6" className="text-center mb-3"><strong>Proof:</strong><div className="mt-2">
                    {expenseDetail.payment_proof.toLowerCase().endsWith(".pdf")?<Button color="primary" size="sm" onClick={()=>window.open(expenseDetail.payment_proof,"_blank")}>📄 View PDF</Button>:<img src={expenseDetail.payment_proof} alt="Proof" className="img-fluid rounded" style={{maxHeight:200}}/>}
                  </div></Col>}
                </Row>
              )}
            </div>
          ):<p className="text-center text-muted my-3">No details available.</p>}
        </ModalBody>
        <ModalFooter>
          {expenseDetail?.payment_status?.toLowerCase()==="unpaid"&&(
            <Button color="danger" onClick={async()=>{
              if(!window.confirm("Delete this unpaid expense?"))return;
              try{await axios.delete(`https://api.hanoch.in/expense/expenses/${expenseDetail.id}/`);setModalOpen(false);fetchExpenses();}
              catch(e){console.error(e);alert("Failed to delete.");}
            }}>Delete</Button>
          )}
          <Button color="secondary" onClick={()=>setModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ExpenseDashboard;
